import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueExpensesStep = ({
  formData,
  onChange,
  errors = {},
  subscriptionTier = 'free',
  onUpgrade,
  isDemo = false
}) => {
  // Always show advanced/detailed mode
  const isProMode = true;

  const handleUnitRevenueChange = (unitIndex, value) => {
    const updatedUnits = [...(formData?.unitRevenues || [])];
    updatedUnits[unitIndex] = value;
    onChange('unitRevenues', updatedUnits);
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    })?.format(value);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8 mb-4 md:mb-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-success)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Revenus et dépenses
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              {isProMode ? 'Analyse détaillée par unité' : 'Estimation globale'}
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {!isProMode ? (
            <>
              {/* Hide upgrade message in demo mode */}
              {!isDemo && (
                <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-warning)" />
                    <div>
                      <p className="text-sm md:text-base text-foreground font-medium mb-1">
                        Mode simplifié
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Passez à Plus ou Pro pour une analyse détaillée par unité avec projections avancées
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Input
                label="Revenus locatifs mensuels totaux"
                type="number"
                placeholder="Ex: 4500"
                value={formData?.totalMonthlyRevenue || ''}
                onChange={(e) => onChange('totalMonthlyRevenue', e?.target?.value)}
                error={errors?.totalMonthlyRevenue}
                required
                min="0"
              />

              <Input
                label="Dépenses mensuelles totales estimées"
                type="number"
                placeholder="Ex: 2000"
                value={formData?.totalMonthlyExpenses || ''}
                onChange={(e) => onChange('totalMonthlyExpenses', e?.target?.value)}
                error={errors?.totalMonthlyExpenses}
                required
                min="0"
              />

              <div className="bg-muted rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm md:text-base text-muted-foreground">
                    Flux de trésorerie mensuel estimé
                  </span>
                  <span className="text-lg md:text-xl font-bold text-foreground data-text">
                    {formatCurrency((formData?.totalMonthlyRevenue || 0) - (formData?.totalMonthlyExpenses || 0))}
                  </span>
                </div>
              </div>

              {/* Hide upgrade button in demo mode */}
              {!isDemo && onUpgrade && (
                <div className="border-t border-border pt-4 md:pt-6">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="Crown"
                    iconPosition="left"
                    onClick={onUpgrade}
                  >
                    Débloquer l'analyse détaillée
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="border-b border-border pb-4 md:pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base md:text-lg font-semibold text-foreground">
                    Revenus locatifs
                  </h3>
                  <Select
                    options={[
                      { value: 'monthly_per_unit', label: 'Mensuel par unité' },
                      { value: 'annual_total', label: 'Annuel brut total' }
                    ]}
                    value={formData?.revenueInputType || 'monthly_per_unit'}
                    onChange={(value) => {
                      onChange('revenueInputType', value);
                      // Clear the other field when switching
                      if (value === 'annual_total') {
                        onChange('unitRevenues', []);
                        onChange('totalMonthlyRevenue', '');
                      } else {
                        onChange('annualTotalRevenue', '');
                      }
                    }}
                    className="w-48"
                  />
                </div>

                {formData?.revenueInputType === 'annual_total' ? (
                  <div className="space-y-3">
                    <Input
                      label="Revenu brut potentiel total annuel"
                      type="number"
                      placeholder="Ex: 72000"
                      value={formData?.annualTotalRevenue || ''}
                      onChange={(e) => {
                        const annualValue = e?.target?.value;
                        onChange('annualTotalRevenue', annualValue);
                        // Calculate monthly equivalent for display
                        const monthlyValue = annualValue ? (parseFloat(annualValue) / 12).toFixed(2) : '';
                        onChange('totalMonthlyRevenue', monthlyValue);
                      }}
                      error={errors?.annualTotalRevenue}
                      required
                      min="0"
                    />
                    {formData?.annualTotalRevenue && (
                      <div className="bg-muted rounded-lg p-3">
                        <p className="text-xs text-muted-foreground">
                          Équivalent mensuel: {new Intl.NumberFormat('fr-CA', {
                            style: 'currency',
                            currency: 'CAD'
                          }).format(parseFloat(formData.annualTotalRevenue) / 12 || 0)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Array.from({ length: parseInt(formData?.units) || 1 })?.map((_, index) => (
                      <Input
                        key={index}
                        label={`Unité ${index + 1} - Loyer mensuel`}
                        type="number"
                        placeholder="Ex: 1200"
                        value={formData?.unitRevenues?.[index] || ''}
                        onChange={(e) => handleUnitRevenueChange(index, e?.target?.value)}
                        error={errors?.[`unitRevenue${index}`]}
                        required
                        min="0"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="border-b border-border pb-4 md:pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base md:text-lg font-semibold text-foreground">
                    Dépenses détaillées
                  </h3>
                  <Select
                    options={[
                      { value: 'monthly', label: 'Mensuel' },
                      { value: 'annual', label: 'Annuel' }
                    ]}
                    value={formData?.expensesFrequency || 'monthly'}
                    onChange={(value) => onChange('expensesFrequency', value)}
                    className="w-32"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={`Taxes municipales (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    tooltip="Montant annuel ou mensuel des taxes municipales et scolaires applicables à l'immeuble."
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 4800' : 'Ex: 400'}
                    value={formData?.propertyTaxes || ''}
                    onChange={(e) => onChange('propertyTaxes', e?.target?.value)}
                    error={errors?.propertyTaxes}
                    required
                    min="0"
                  />

                  <Input
                    label={`Assurance (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    tooltip="Coût de l'assurance responsabilité civile et de l'assurance bâtiment."
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 1800' : 'Ex: 150'}
                    value={formData?.insurance || ''}
                    onChange={(e) => onChange('insurance', e?.target?.value)}
                    error={errors?.insurance}
                    required
                    min="0"
                  />

                  <Input
                    label={`Électricité (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    tooltip="Frais d'électricité à la charge du propriétaire (aires communes ou logements non chauffés/éclairés)."
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 2400' : 'Ex: 200'}
                    value={formData?.electricity || ''}
                    onChange={(e) => onChange('electricity', e?.target?.value)}
                    error={errors?.electricity}
                    min="0"
                  />

                  <Input
                    label={`Chauffage (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    tooltip="Frais de chauffage (gaz, huile ou électricité) si payés par le propriétaire."
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 3000' : 'Ex: 250'}
                    value={formData?.heating || ''}
                    onChange={(e) => onChange('heating', e?.target?.value)}
                    error={errors?.heating}
                    min="0"
                  />

                  <Input
                    label={`Eau (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 960' : 'Ex: 80'}
                    value={formData?.water || ''}
                    onChange={(e) => onChange('water', e?.target?.value)}
                    error={errors?.water}
                    min="0"
                  />

                  <Input
                    label={`Entretien et réparations (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    tooltip="Budget prévu pour les réparations courantes et le maintien en bon état de l'immeuble."
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 3600' : 'Ex: 300'}
                    value={formData?.maintenance || ''}
                    onChange={(e) => onChange('maintenance', e?.target?.value)}
                    error={errors?.maintenance}
                    min="0"
                  />

                  <Input
                    label="Gestion immobilière (%)"
                    tooltip="Frais payés à une entreprise pour gérer les locataires et l'immeuble."
                    type="number"
                    placeholder="Ex: 8"
                    value={formData?.managementFeePercent || ''}
                    onChange={(e) => onChange('managementFeePercent', e?.target?.value)}
                    error={errors?.managementFeePercent}
                    min="0"
                    max="100"
                  />

                  <Input
                    label={`Autres dépenses (${formData?.expensesFrequency === 'annual' ? 'annuel' : 'mensuel'})`}
                    type="number"
                    placeholder={formData?.expensesFrequency === 'annual' ? 'Ex: 1200' : 'Ex: 100'}
                    value={formData?.otherExpenses || ''}
                    onChange={(e) => onChange('otherExpenses', e?.target?.value)}
                    error={errors?.otherExpenses}
                    min="0"
                  />
                </div>
              </div>

              <div className="border-b border-border pb-4 md:pb-6">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                  Paramètres de projection
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Taux d'inoccupation (%)"
                    tooltip="Pourcentage de loyer perdu si l'immeuble n'est pas loué à 100% toute l'année (souvent 3% à 5%)."
                    type="number"
                    placeholder="Ex: 5"
                    value={formData?.vacancyRate || ''}
                    onChange={(e) => onChange('vacancyRate', e?.target?.value)}
                    error={errors?.vacancyRate}
                    required
                    min="0"
                    max="100"
                  />

                  <Input
                    label="Taux de perception (%)"
                    tooltip="Pourcentage des loyers que vous réussissez réellement à collecter après les mauvais créanciers."
                    type="number"
                    placeholder="Ex: 98"
                    value={formData?.collectionRate || ''}
                    onChange={(e) => onChange('collectionRate', e?.target?.value)}
                    error={errors?.collectionRate}
                    required
                    min="0"
                    max="100"
                  />

                  <Input
                    label="Croissance des loyers annuelle (%)"
                    tooltip="Augmentation annuelle estimée des revenus de loyer au fil des ans."
                    type="number"
                    placeholder="Ex: 2.5"
                    value={formData?.rentGrowthRate || ''}
                    onChange={(e) => onChange('rentGrowthRate', e?.target?.value)}
                    error={errors?.rentGrowthRate}
                    required
                    min="0"
                    max="20"
                    step="0.1"
                  />

                  <Input
                    label="Appréciation de la valeur annuelle (%)"
                    tooltip="Augmentation annuelle estimée de la valeur marchande de l'immeuble (souvent 2% à 4%)."
                    type="number"
                    placeholder="Ex: 3"
                    value={formData?.appreciationRate || ''}
                    onChange={(e) => onChange('appreciationRate', e?.target?.value)}
                    error={errors?.appreciationRate}
                    required
                    min="0"
                    max="20"
                    step="0.1"
                  />
                </div>
              </div>

              <div className="bg-success/5 border border-success/20 rounded-lg p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs md:text-sm text-muted-foreground block mb-1">
                      Revenus mensuels totaux
                    </span>
                    <span className="text-lg md:text-xl font-bold text-foreground data-text">
                      {formatCurrency(
                        formData?.revenueInputType === 'annual_total' && formData?.annualTotalRevenue
                          ? parseFloat(formData.annualTotalRevenue) / 12 || 0
                          : formData?.unitRevenues?.reduce((sum, val) => sum + (parseFloat(val) || 0), 0) || 0
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs md:text-sm text-muted-foreground block mb-1">
                      Dépenses mensuelles totales
                    </span>
                    <span className="text-lg md:text-xl font-bold text-foreground data-text">
                      {formatCurrency(
                        (parseFloat(formData?.propertyTaxes) || 0) +
                        (parseFloat(formData?.insurance) || 0) +
                        (parseFloat(formData?.electricity) || 0) +
                        (parseFloat(formData?.heating) || 0) +
                        (parseFloat(formData?.water) || 0) +
                        (parseFloat(formData?.maintenance) || 0) +
                        (parseFloat(formData?.otherExpenses) || 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueExpensesStep;